import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
    inject,
    ChangeDetectorRef,
    ElementRef
} from "@angular/core";
import { Subject, asapScheduler, filter } from "rxjs";

import { LgNavigationService } from "@logex/framework/lg-application";
import { LgObserveSizeService } from "@logex/framework/ui-core";
import { toBoolean } from "@logex/framework/utilities";

import { LgSidebarService } from "./lg-sidebar.service";
import { SidebarButton } from "./sidebar-button-base";
import { ISidebar } from "./sidebar-context";
import { LG_FW_UI_STATE_SERVICE } from "../lg-fw-ui-state/lg-fw-ui-state.types";
import { distinctUntilChanged, map, observeOn, takeUntil, takeWhile } from "rxjs/operators";
import { LgGlobalShortcutsService } from "../lg-global-shortcuts/lg-global-shortcuts.service";

@Component({
    // eslint-disable-next-line @angular-eslint/prefer-standalone
    standalone: false,
    selector: "lg-sidebar",
    templateUrl: "./lg-sidebar.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "lg-sidebar",
        "[class.lg-sidebar--pinned]": "_pinned && _visible"
    },
    encapsulation: ViewEncapsulation.None
})
export class LgSidebarComponent implements OnInit, OnDestroy, ISidebar {
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _service = inject(LgSidebarService);
    private _sizeObserver = inject(LgObserveSizeService);
    private _uiState = inject(LG_FW_UI_STATE_SERVICE);
    private _globalShortcuts = inject(LgGlobalShortcutsService);
    private _navigationService = inject(LgNavigationService);
    private _root = inject(ElementRef<HTMLElement>);

    @Output("pinToggled") readonly pinToggled = new EventEmitter<boolean>();

    @Input()
    set showNameAsTooltip(value: boolean) {
        this._showNameAsTooltip = toBoolean(value);
    }

    get showNameAsTooltip(): boolean {
        return this._showNameAsTooltip;
    }

    protected _topWidgets = this._service.topWidgets().pipe(observeOn(asapScheduler));
    protected _bottomWidgets = this._service.bottomWidgets().pipe(
        map(widgets => [...widgets].reverse()),
        observeOn(asapScheduler)
    );

    protected _pinned = false;
    protected _visible = false;
    protected _showNameAsTooltip = false;
    protected _selectedButton: SidebarButton | null = null;
    protected _shownBySavedState = true;

    private _lastNavPath: Array<{ id?: string | undefined }> = [];
    private $destroyed = new Subject<void>();
    private _unregisterShortcut = this._globalShortcuts.registerStandardComponent(
        "sidebar",
        this._root
    );

    constructor() {
        this._globalShortcuts.shortcutTriggered$
            .pipe(
                takeUntil(this.$destroyed),
                filter(event => event !== "sidebar")
            )
            .subscribe(() => {
                this.dismissPanel();
                this._changeDetectorRef.markForCheck();
            });

        this._navigationService
            .currentNodePath$()
            .pipe(takeUntil(this.$destroyed))
            .subscribe(path => {
                this._lastNavPath = path;
                this._syncPanelWithNavigation(path);
            });
    }

    async ngOnInit(): Promise<void> {
        await this._initWithUiState();
    }

    ngOnDestroy(): void {
        this._unregisterShortcut?.();
        this.$destroyed.next();
        this.$destroyed.complete();
    }

    _toggle(button: SidebarButton): void {
        if (button.disabled) return;
        if (this._selectedButton === button) {
            button.clicked(false);
            this._selectedButton = null;
            this._setUiStateSidebarButton(null);
        } else if (button.panelTemplate) {
            button.clicked(true);
            this._visible = true;
            this._selectedButton = button;
            this._shownBySavedState = false;
            this._setUiStateSidebarButton(button);
        } else {
            button.clicked(undefined);
            if (button.url) {
                window.location.replace(button.url);
            }
        }
    }

    _togglePin(): void {
        this._pinned = !this._pinned;
        this._uiState.setLeftSidebarPinned(this._pinned);
        this._sizeObserver.recalculate();
        this.pinToggled.emit(this._pinned);
    }

    _panelHidden(): void {
        if (this._selectedButton === null) this._visible = false;
    }

    _clamp(value: number): number {
        return Math.min(value, 99);
    }

    dismissPanel(): void {
        if (this._selectedButton && !this._pinned) {
            this._toggle(this._selectedButton);
        }
    }

    protected _escape(): void {
        this.dismissPanel();
        this._root.nativeElement.querySelector(".lg-sidebar-btn--active")?.focus();
    }

    private async _initWithUiState(): Promise<void> {
        const pinned = await this._uiState.getLeftSidebarPinned();
        if (pinned == null) return;

        this._pinned = pinned;
        const initButton = await this._uiState.getSidebarButton();
        let initFinished = false;

        this.pinToggled.emit(this._pinned);
        this._changeDetectorRef.markForCheck();

        this._topWidgets
            .pipe(
                takeWhile(() => !initFinished),
                takeUntil(this.$destroyed),
                distinctUntilChanged()
            )
            .subscribe(widgets => {
                if (this._pinned && initButton?.top) {
                    const initSidebarButton = widgets.find(
                        widget => widget.id === initButton?.id
                    ) as SidebarButton;

                    if (initSidebarButton && initSidebarButton.panelTemplate) {
                        initSidebarButton.clicked(true);
                        this._selectedButton = initSidebarButton;
                        this._visible = true;
                        initFinished = true;
                        this._syncPanelWithNavigation(this._lastNavPath);
                    }
                    this._changeDetectorRef.markForCheck();
                }
            });

        this._bottomWidgets
            .pipe(
                takeWhile(() => !initFinished),
                takeUntil(this.$destroyed),
                distinctUntilChanged()
            )
            .subscribe(widgets => {
                if (this._pinned && !initButton?.top) {
                    const initSidebarButton = widgets.find(
                        widget => widget.id === initButton?.id
                    ) as SidebarButton;

                    if (initSidebarButton && initSidebarButton.panelTemplate) {
                        initSidebarButton.clicked(true);
                        this._selectedButton = initSidebarButton;
                        this._visible = true;
                        initFinished = true;
                        this._syncPanelWithNavigation(this._lastNavPath);
                    }
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    private _syncPanelWithNavigation(path: Array<{ id?: string | undefined }>): void {
        if (!this._pinned || !this._visible || !this._selectedButton) return;

        const allWidgets = this._service.getAllWidgets();

        for (const node of path) {
            if (!node.id) continue;

            const matchingButton = allWidgets.find(w => {
                const button = w as SidebarButton;
                const navId = button.navigationRootId || button.id;
                return navId === node.id;
            }) as SidebarButton | undefined;

            if (
                matchingButton &&
                matchingButton !== this._selectedButton &&
                matchingButton.panelTemplate &&
                !matchingButton.disabled &&
                matchingButton.isVisible
            ) {
                matchingButton.clicked(true);
                this._visible = true;
                this._selectedButton = matchingButton;
                this._shownBySavedState = true;
                this._changeDetectorRef.markForCheck();
                return;
            }
        }
    }

    private _setUiStateSidebarButton(button: SidebarButton | null): void {
        if (button?.id) {
            this._uiState.setSidebarButton({
                top: button.onTop,
                id: button.id
            });
        } else {
            this._uiState.setSidebarButton(null);
        }
    }
}
